import type { SearchableSelectOption } from "@racoon/ui";

/** The fields of an organization the tree needs; ids may arrive as bigint or string. */
export interface TreeOrganization {
  id?: bigint | string | number;
  parentId?: bigint | string | number;
  name?: string;
}

/**
 * Orders a subtree (as ListOrganizationSubtree returns it, root first) depth-first, each
 * node followed by its children sorted by name, and tags every option with its depth.
 * A node whose parent is not in the list is treated as a root, so nothing is dropped.
 */
export function organizationTreeOptions(orgs: TreeOrganization[]): SearchableSelectOption[] {
  const key = (v: TreeOrganization["id"]) => (v === undefined || v === null ? "" : String(v));
  const present = new Set(orgs.map((o) => key(o.id)));
  const children = new Map<string, TreeOrganization[]>();
  const roots: TreeOrganization[] = [];
  for (const org of orgs) {
    const parent = key(org.parentId);
    if (parent && parent !== key(org.id) && present.has(parent)) {
      children.set(parent, [...(children.get(parent) ?? []), org]);
    } else {
      roots.push(org);
    }
  }
  const byName = (a: TreeOrganization, b: TreeOrganization) =>
    (a.name ?? "").localeCompare(b.name ?? "");

  const options: SearchableSelectOption[] = [];
  const seen = new Set<string>();
  const visit = (org: TreeOrganization, depth: number) => {
    const id = key(org.id);
    // A parent_id cycle must not loop forever.
    if (seen.has(id)) return;
    seen.add(id);
    options.push({ id, name: org.name ?? "", depth });
    for (const child of [...(children.get(id) ?? [])].sort(byName)) visit(child, depth + 1);
  };
  // Roots keep the server's order: the requested root comes first. A parent_id cycle has
  // no root above it, so after the tree it is entered once at its first listed member,
  // with the rest nested below.
  roots.forEach((root) => visit(root, 0));
  orgs.forEach((org) => visit(org, 0));
  return options;
}
